import { Field } from '@lucidtech/las-sdk-browser';
import { createContext } from 'react';
import { createStore } from 'zustand';
import { devtools } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';

import { AgentRunId } from '@/hooks/api';

export type TableValidationState = {
  field: Field;
  label: string;
};

export interface ValidationProps {
  recentlyCompleted?: AgentRunId[];
  currentTable?: TableValidationState;
  tableViewIsHidden?: boolean;
  detachTable?: boolean;
  scrollToPage?: OnPageScrollHandler;
  parentContainerWidth?: number;
  parentContainerHeight?: number;
}

export type OnPageScrollHandler = (page: number) => void;

export interface ValidationState extends ValidationProps {
  addRecentlyCompleted: (agentRun: AgentRunId | undefined) => void;
  onScrollToPage: (callback: OnPageScrollHandler) => void;
  setDetachTable: (v: boolean) => void;
  hideTableView: (v: boolean) => void;
  setCurrentTable: (label: string, field: Field) => void;
  setParentContainerSize: (width: number, height: number) => void;
}

export const createValidationStore = (initProps?: Partial<ValidationProps>) => {
  return createStore<ValidationState>()(
    devtools(
      immer((set) => ({
        ...initProps,
        addRecentlyCompleted: (agentRun) =>
          set((state) => {
            if (agentRun && state.recentlyCompleted) {
              state.recentlyCompleted = [...state.recentlyCompleted, agentRun];
            }
          }),
        hideTableView: (shouldHide: boolean) =>
          set((state) => {
            state.tableViewIsHidden = shouldHide;
          }),
        setDetachTable: (shouldDetach: boolean) =>
          set((state) => {
            state.detachTable = shouldDetach;
          }),
        onScrollToPage: (handler: OnPageScrollHandler) =>
          set((state) => {
            state.scrollToPage = handler;
          }),
        setCurrentTable: (label: string, field: Field) =>
          set((state) => {
            state.currentTable = { label, field };
          }),
        setParentContainerSize: (width: number, height: number) =>
          set((state) => {
            state.parentContainerWidth = width;
            state.parentContainerHeight = height;
          }),
      }))
    )
  );
};

export type ValidationStore = ReturnType<typeof createValidationStore>;

export const ValidationContext = createContext<ValidationStore | null>(null);
